import { formatTimestamp } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { WizardState } from "@/types/wizard";
import { getWizardVerdict, isBootstrapWorkPending, type WizardVerdict } from "@/lib/wizardStatus";

export interface ReviewBanner {
  scenarioLabel: string;
  statusLabel: string;
  statusVariant: "warning" | "success" | "outline";
  description: string;
  verdict: WizardVerdict;
}

export function getReviewPageDescription(input: {
  bootstrapWorkPending: boolean;
  hasApply: boolean;
}): string {
  if (input.hasApply) {
    return strings.pages.review.descriptionAfterApply;
  }

  if (!input.bootstrapWorkPending) {
    return strings.pages.review.descriptionSystemOk;
  }

  return strings.pages.review.description;
}

export function getReviewBanner(input: {
  state: WizardState | undefined;
  bootstrapWorkPending: boolean;
  hasApply: boolean;
  pendingCount: number;
}): ReviewBanner {
  const verdict = getWizardVerdict(input.state, undefined);

  if (input.hasApply) {
    return {
      verdict,
      scenarioLabel: strings.pages.review.bannerApplied,
      statusLabel: strings.pages.review.statusApplied,
      statusVariant: "success",
      description: strings.pages.review.appliedDescription,
    };
  }

  if (!input.bootstrapWorkPending) {
    return {
      verdict,
      scenarioLabel: strings.pages.review.bannerSystemOk,
      statusLabel: strings.pages.review.statusNoApplyNeeded,
      statusVariant: "success",
      description: strings.pages.review.systemOkOnReview(
        formatTimestamp(input.state?.lastAppliedAt),
        input.pendingCount,
      ),
    };
  }

  if (input.state?.updatePending) {
    return {
      verdict,
      scenarioLabel: strings.pages.review.bannerConfigUpdate,
      statusLabel: strings.pages.review.statusApplyRequired,
      statusVariant: "warning",
      description:
        input.pendingCount > 0
          ? strings.pages.review.configUpdateWithChanges
          : strings.pages.review.configUpdateNoChanges,
    };
  }

  return {
    verdict,
    scenarioLabel: strings.pages.review.bannerFirstInstall,
    statusLabel: strings.pages.review.statusApplyRequired,
    statusVariant: "warning",
    description:
      input.pendingCount > 0
        ? strings.pages.review.firstInstallWithChanges
        : strings.pages.review.firstInstallNoChanges,
  };
}

export function getNoPendingHint(input: {
  bootstrapWorkPending: boolean;
  updatePending: boolean;
}): string {
  if (!input.bootstrapWorkPending) {
    return strings.pages.review.noPendingHintSystemOk;
  }

  if (input.updatePending) {
    return strings.pages.review.noPendingHintConfigUpdate;
  }

  return strings.pages.review.noPendingHintFirstInstall;
}

export function getApplyHint(input: {
  bootstrapWorkPending: boolean;
  hasApply: boolean;
  pendingCount: number;
}): string {
  if (input.hasApply) {
    return strings.pages.review.applyHintAfterApply;
  }

  if (!input.bootstrapWorkPending) {
    return strings.pages.review.applyHintSystemOk;
  }

  if (input.pendingCount === 0) {
    return strings.pages.review.applyHintNoPending;
  }

  return strings.pages.review.applyHint;
}

export function getApplyButtonLabel(input: {
  isApplying: boolean;
  hasApply: boolean;
  bootstrapWorkPending: boolean;
  firstStartPending: boolean;
}): string {
  if (input.isApplying) {
    return strings.pages.review.applying;
  }

  if (input.hasApply) {
    return strings.pages.review.alreadyApplied;
  }

  if (!input.bootstrapWorkPending) {
    return strings.pages.review.applyOptional;
  }

  if (input.firstStartPending) {
    return strings.pages.review.applyFirstInstall;
  }

  return strings.pages.review.applyChanges;
}

export { isBootstrapWorkPending };
